import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CRM inbox emails (sequence emails, tracked emails).
 * Polls every 60s for new items.
 */
export function useInboxEmails() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['inbox', 'emails'],
        queryFn: () => apiFetch('/inbox/emails', getAuthHeader()),
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
        select: (data) => ({
            emails: data?.emails || [],
            counts: data?.counts || { primary: 0, emails: 0, calls: 0, messages: 0, tasks: 0, reminders: 0 },
        }),
    });
}

/**
 * All CRM communications (calls, messages, tasks, reminders).
 * Polls every 60s.
 */
export function useCommunications() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['communications'],
        queryFn: async () => {
            const data = await apiFetch('/communications', getAuthHeader());
            return Array.isArray(data) ? data : (data?.data || []);
        },
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
    });
}

/**
 * Gmail OAuth connection status.
 */
export function useGmailStatus() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['gmail', 'status'],
        queryFn: async () => {
            try {
                const data = await apiFetch('/oauth/gmail/status', getAuthHeader());
                return { connected: data.connected, email: data.gmail_email || '' };
            } catch {
                return { connected: false, email: '' };
            }
        },
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * Gmail emails from the connected Gmail account.
 * Only enabled when Gmail is connected. Polls every 60s.
 */
export function useGmailEmails(gmailConnected) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['gmail', 'emails'],
        queryFn: async () => {
            const data = await apiFetch('/oauth/gmail/emails?max_results=30', getAuthHeader());
            return (data.emails || []).map(e => ({
                ...e,
                _source: 'gmail',
                email_id: `gmail_${e.id}`,
                contact_name: e.from?.replace(/<.*>/, '').trim() || '',
                contact_email: e.from?.match(/<(.+)>/)?.[1] || e.from || '',
                subject: e.subject,
                body: e.snippet,
                status: e.is_unread ? 'unread' : 'read',
                sent_at: e.date,
            }));
        },
        enabled: !!gmailConnected,
        staleTime: 2 * 60 * 1000,
        refetchInterval: 60 * 1000,
    });
}

/**
 * Email templates (personal + admin).
 */
export function useEmailTemplates() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['email-templates'],
        queryFn: () => apiFetch('/email-templates', getAuthHeader()),
        staleTime: 10 * 60 * 1000,
        select: (data) => ({
            templates: data?.templates || [],
            adminTemplates: data?.admin_templates || [],
        }),
    });
}

/**
 * Email signatures (personal + admin).
 */
export function useSignatures() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['signatures'],
        queryFn: () => apiFetch('/signatures', getAuthHeader()),
        staleTime: 10 * 60 * 1000,
        select: (data) => ({
            signatures: data?.signatures || [],
            adminSignatures: data?.admin_templates || [],
            defaultSignature: (data?.signatures || []).find(s => s.is_default) || null,
        }),
    });
}

/**
 * Google Calendar connection status.
 */
export function useCalendarStatus() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['calendar', 'status'],
        queryFn: async () => {
            try {
                const data = await apiFetch('/oauth/calendar/status', getAuthHeader());
                return { connected: data?.connected || false };
            } catch {
                return { connected: false };
            }
        },
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * Calendar events / meetings for a specific date.
 */
export function useCalendarEvents(date, calendarConnected) {
    const { getAuthHeader } = useAuth();
    const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;

    return useQuery({
        queryKey: ['calendar', 'events', dateStr],
        queryFn: async () => {
            if (calendarConnected) {
                const start = new Date(date);
                start.setHours(0, 0, 0, 0);
                const end = new Date(date);
                end.setHours(23, 59, 59, 999);
                const data = await apiFetch(
                    `/oauth/calendar/events?time_min=${start.toISOString()}&time_max=${end.toISOString()}`,
                    getAuthHeader()
                );
                return (data.events || []).map(ev => ({ ...ev, source: 'google_calendar' }));
            } else {
                const data = await apiFetch(`/activities?type=meeting&date=${dateStr}`, getAuthHeader());
                const arr = Array.isArray(data) ? data : (data?.data || []);
                return arr.filter(a => a.type === 'meeting').map(m => ({
                    event_id: m.activity_id,
                    title: m.subject,
                    start: m.due_date,
                    source: 'crm',
                }));
            }
        },
        staleTime: 2 * 60 * 1000,
    });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

/**
 * Send email mutation (via Gmail or log as communication).
 */
export function useSendEmail() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ to, subject, body, cc, bcc, gmailConnected, outlookConnected, replyToId, track = false, contact_id, account_id }) => {
            const ccEmails = (cc || []).map(c => c.email).filter(Boolean);
            const bccEmails = (bcc || []).map(c => c.email).filter(Boolean);

            if (gmailConnected) {
                return axios.post(`${API}/oauth/gmail/send`, {
                    to, subject, body, track,
                    ...(contact_id && { contact_id }),
                    ...(account_id && { account_id }),
                    ...(ccEmails.length > 0 && { cc: ccEmails }),
                    ...(bccEmails.length > 0 && { bcc: bccEmails }),
                }, { headers: getAuthHeader() });
            } else if (outlookConnected) {
                return axios.post(`${API}/oauth/outlook/send`, {
                    to, subject, body, track,
                    ...(contact_id && { contact_id }),
                    ...(account_id && { account_id }),
                    ...(ccEmails.length > 0 && { cc: ccEmails }),
                    ...(bccEmails.length > 0 && { bcc: bccEmails }),
                }, { headers: getAuthHeader() });
            } else {
                return axios.post(`${API}/communications`, {
                    type: 'email', direction: 'outbound',
                    subject, body, to_emails: [to],
                    ...(ccEmails.length > 0 && { cc_emails: ccEmails }),
                    ...(bccEmails.length > 0 && { bcc_emails: bccEmails }),
                }, { headers: getAuthHeader() });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['gmail', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['outlook', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['communications'] });
            queryClient.invalidateQueries({ queryKey: ['tracking'] });
        },
    });
}

/**
 * Tracked emails with open/click/reply stats.
 */
export function useTrackedEmails(page = 1, pageSize = 25) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['tracking', 'emails', page, pageSize],
        queryFn: async () => {
            const data = await apiFetch(`/tracking/emails?page=${page}&page_size=${pageSize}`, getAuthHeader());
            return data;
        },
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    });
}

/**
 * Single tracked email detail with full event timeline.
 */
export function useTrackedEmailDetail(trackingId) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['tracking', 'email', trackingId],
        queryFn: () => apiFetch(`/tracking/emails/${trackingId}`, getAuthHeader()),
        enabled: !!trackingId,
        staleTime: 15 * 1000,
    });
}

/**
 * Sync Gmail emails to CRM.
 */
export function useSyncGmail() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            axios.get(`${API}/oauth/gmail/sync`, {
                headers: getAuthHeader(),
                params: { max_results: 50 },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['gmail', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['communications'] });
        },
    });
}

/**
 * Log a communication (call, message, task, reminder).
 */
export function useLogCommunication() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (logForm) =>
            axios.post(`${API}/communications`, logForm, { headers: getAuthHeader() }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications'] });
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
        },
    });
}

/**
 * Archive an inbox email.
 */
export function useArchiveEmail() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (emailId) =>
            axios.put(`${API}/inbox/emails/${emailId}/archive`, {}, { headers: getAuthHeader() }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
        },
    });
}

/**
 * Create a contact from an email.
 */
export function useAddContactFromEmail() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (email) =>
            axios.post(`${API}/contacts`, {
                first_name: email.contact_name?.split(' ')[0] || 'Unknown',
                last_name: email.contact_name?.split(' ').slice(1).join(' ') || '',
                email: email.contact_email,
                account_id: email.account_id,
                source: 'Email Inbox',
            }, { headers: getAuthHeader() }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
        },
    });
}
